<template>
  <div class="border-b border-gray-200 dark:border-gray-700 pb-4">
    <!-- Compact Info Grid -->
    <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
      <!-- Begin datum - editable -->
      <div>
        <label
          class="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1"
          >Start</label
        >
        <!-- View mode -->
        <div
          v-if="!isEditingStartDate"
          class="text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-1 -mx-1 inline-flex items-center gap-1"
          :class="
            isStartInPast ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
          "
          @click="startEditingStart"
          title="Klik om te bewerken"
        >
          {{ formatDate(prospect.start_date) || '-' }}
          <PencilIcon class="h-3 w-3 text-gray-400" />
        </div>
        <!-- Edit mode -->
        <div v-else class="space-y-2">
          <DatePickerLite
            :model-value="editingStartDate"
            @update:model-value="editingStartDate = $event"
            placeholder="Selecteer datum"
          />
          <div class="flex gap-1">
            <button
              @click="saveStartDate"
              :disabled="isSaving"
              class="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {{ isSaving ? 'Opslaan...' : 'Opslaan' }}
            </button>
            <button
              @click="cancelStartEdit"
              :disabled="isSaving"
              class="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Annuleren
            </button>
          </div>
        </div>
      </div>

      <!-- Eind datum - editable -->
      <div>
        <label
          class="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1"
          >Eind</label
        >
        <!-- View mode -->
        <div
          v-if="!isEditingEndDate"
          class="text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-1 -mx-1 inline-flex items-center gap-1"
          :class="endDateClass"
          @click="startEditingEnd"
          title="Klik om te bewerken"
        >
          {{ formatDate(prospect.expected_closing_date) || '-' }}
          <PencilIcon class="h-3 w-3 text-gray-400" />
        </div>
        <!-- Edit mode -->
        <div v-else class="space-y-2">
          <DatePickerLite
            :model-value="editingEndDate"
            @update:model-value="editingEndDate = $event"
            placeholder="Selecteer datum"
          />
          <div class="flex gap-1">
            <button
              @click="saveEndDate"
              :disabled="isSaving"
              class="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {{ isSaving ? 'Opslaan...' : 'Opslaan' }}
            </button>
            <button
              @click="cancelEndEdit"
              :disabled="isSaving"
              class="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Annuleren
            </button>
          </div>
        </div>
      </div>

      <!-- Omzet -->
      <div>
        <label
          class="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1"
          >Omzet</label
        >
        <div class="text-sm font-medium text-green-600 dark:text-green-400">
          {{ formatCurrency(prospect.expected_revenue) }}
        </div>
      </div>

      <!-- Slagingskans - editable + ML prediction -->
      <div>
        <label
          class="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1"
          >Kans</label
        >
        <!-- View mode -->
        <div v-if="!isEditingChance" class="space-y-1">
          <div
            class="text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-1 -mx-1 inline-flex items-center gap-1 text-gray-900 dark:text-white"
            @click="startEditingChance"
            title="Klik om te bewerken"
          >
            {{ prospect.chance_to_score ?? '-' }}%
            <PencilIcon class="h-3 w-3 text-gray-400" />
          </div>
          <!-- ML prediction badge -->
          <div v-if="mlPrediction" class="flex items-center gap-1">
            <SparklesIcon class="h-3.5 w-3.5" :class="mlColorClass" />
            <span class="text-xs font-medium" :class="mlColorClass">
              ML: {{ Math.round(mlPrediction.predicted_probability * 100) }}%
            </span>
            <span v-if="mlDifference !== null" class="text-xs" :class="mlDiffClass">
              ({{ mlDifference > 0 ? '+' : '' }}{{ mlDifference }}%)
            </span>
            <button
              @click="showMlDetails = !showMlDetails"
              class="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline ml-1"
            >
              {{ showMlDetails ? 'verberg' : 'details' }}
            </button>
          </div>
          <!-- ML feature detail panel -->
          <div
            v-if="showMlDetails && mlPrediction"
            class="mt-1 p-2 bg-gray-50 dark:bg-gray-700/50 rounded text-xs space-y-1"
          >
            <div class="font-medium text-gray-700 dark:text-gray-300">Feature bijdragen:</div>
            <div
              v-for="(value, name) in mlPrediction.feature_contributions"
              :key="name"
              class="flex justify-between"
            >
              <span class="text-gray-600 dark:text-gray-400">{{ featureLabel(name) }}</span>
              <span
                :class="
                  value >= 0
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                "
              >
                {{ value >= 0 ? '+' : '' }}{{ value.toFixed(3) }}
              </span>
            </div>
          </div>
        </div>
        <!-- Edit mode -->
        <div v-else class="space-y-2">
          <input
            v-model.number="editingChance"
            type="number"
            min="1"
            max="100"
            step="1"
            class="w-20 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            @keyup.enter="saveChance"
            @keyup.escape="cancelChanceEdit"
          />
          <div class="flex gap-1">
            <button
              @click="saveChance"
              :disabled="isSaving"
              class="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {{ isSaving ? 'Opslaan...' : 'Opslaan' }}
            </button>
            <button
              @click="cancelChanceEdit"
              :disabled="isSaving"
              class="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Annuleren
            </button>
          </div>
        </div>
      </div>

      <!-- Verantwoordelijk Team -->
      <div v-if="teams && teams.length > 0">
        <label
          class="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1"
        >
          Team
          <span class="relative group cursor-help">
            <InformationCircleIcon class="h-3.5 w-3.5 text-gray-400" />
            <span
              class="absolute left-0 top-full mt-1.5 hidden group-hover:block w-56 px-2.5 py-1.5 text-xs font-normal normal-case tracking-normal text-white bg-gray-800 dark:bg-gray-900 rounded shadow-lg z-50 pointer-events-none"
            >
              Per saleskans is 1 PL verantwoordelijk voor sourcing. Dit is de PL van het team waar
              het zwaartepunt ligt.
            </span>
          </span>
        </label>
        <!-- Editable mode -->
        <select
          v-if="canEditTeam"
          :value="responsibleTeamId || ''"
          @change="onUpdateTeam?.($event.target.value || null)"
          class="w-full text-sm rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 border"
          :class="
            teamError && !responsibleTeamId
              ? 'border-red-500 dark:border-red-400 ring-1 ring-red-500'
              : 'border-gray-300 dark:border-gray-600'
          "
        >
          <option value="">Selecteer team...</option>
          <option v-for="team in teams" :key="team.simplicate_id" :value="team.simplicate_id">
            {{ team.name }}
          </option>
        </select>
        <!-- Read-only mode -->
        <div v-else class="text-sm text-gray-900 dark:text-white">
          {{ selectedTeamName || '-' }}
        </div>
      </div>
    </div>

    <!-- Error message -->
    <div v-if="errorMessage" class="mt-2 text-sm text-red-600 dark:text-red-400">
      {{ errorMessage }}
    </div>

    <!-- Sync status message -->
    <div v-if="syncStatus" class="mt-2 text-xs" :class="syncStatusClass">
      {{ syncStatus }}
    </div>

    <!-- Link naar Simplicate -->
    <div class="mt-3 flex items-center gap-4">
      <a
        class="text-xs text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
        :href="simplicateSaleUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
          />
        </svg>
        Bekijk in Simplicate
      </a>
      <span
        v-if="endDateClass !== 'text-gray-900 dark:text-white'"
        class="text-xs text-red-600 dark:text-red-400"
      >
        Let op: controleer de datums
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { PencilIcon, SparklesIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'
import DatePickerLite from '@/components/common/inputs/DatePickerLite.vue'

const props = defineProps({
  prospect: {
    type: Object,
    required: true
  },
  workflowStatus: {
    type: Object,
    default: null
  },
  onUpdateDates: {
    type: Function,
    default: null
  },
  onUpdateChanceToScore: {
    type: Function,
    default: null
  },
  mlPrediction: {
    type: Object,
    default: null
  },
  teams: {
    type: Array,
    default: null
  },
  responsibleTeamId: {
    type: String,
    default: null
  },
  canEditTeam: {
    type: Boolean,
    default: false
  },
  onUpdateTeam: {
    type: Function,
    default: null
  },
  teamError: {
    type: Boolean,
    default: false
  }
})

const selectedTeamName = computed(() => {
  if (!props.responsibleTeamId || !props.teams) return null
  const team = props.teams.find(t => t.simplicate_id === props.responsibleTeamId)
  return team?.name || null
})

// ML detail panel state
const showMlDetails = ref(false)

// ML computed properties
const mlColorClass = computed(() => {
  const prob = props.mlPrediction?.predicted_probability
  if (prob == null) return 'text-gray-400'
  if (prob >= 0.7) return 'text-green-600 dark:text-green-400'
  if (prob >= 0.4) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
})

const mlDifference = computed(() => {
  if (!props.mlPrediction || props.prospect.chance_to_score == null) return null
  const mlPct = Math.round(props.mlPrediction.predicted_probability * 100)
  return mlPct - props.prospect.chance_to_score
})

const mlDiffClass = computed(() => {
  if (mlDifference.value == null) return ''
  return mlDifference.value >= 0
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400'
})

// Feature name translations
const featureLabels = {
  chance_to_score_normalized: 'Handmatige kans',
  expected_revenue: 'Verwachte omzet',
  duration_days: 'Looptijd (dagen)',
  progress_lead_fase: 'Lead fase',
  progress_prospect_fase: 'Prospect fase',
  progress_verkenningsfase: 'Verkenningsfase',
  employee_conversion_rate: 'Commerciant conversie'
}

function featureLabel(name) {
  return featureLabels[name] || name
}

// Edit state
const isEditingStartDate = ref(false)
const isEditingEndDate = ref(false)
const isEditingChance = ref(false)
const editingStartDate = ref('')
const editingEndDate = ref('')
const editingChance = ref(null)
const isSaving = ref(false)
const errorMessage = ref('')
const syncStatus = ref('')

// Format date for display
const formatDate = dateString => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

// Format date for editing (YYYY-MM-DD)
const toDateString = dateString => {
  if (!dateString) return ''
  return dateString.split('T')[0]
}

const formatCurrency = amount => {
  if (!amount) return '€0'
  return `€${new Intl.NumberFormat('nl-NL').format(amount)}`
}

const isStartInPast = computed(() => {
  if (!props.prospect.start_date) return false
  const startDate = new Date(props.prospect.start_date)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return startDate < today
})

const endDateClass = computed(() => {
  if (!props.prospect.expected_closing_date || !props.prospect.start_date) {
    return 'text-gray-900 dark:text-white'
  }
  const endDate = new Date(props.prospect.expected_closing_date)
  const startDate = new Date(props.prospect.start_date)
  return endDate < startDate ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
})

const syncStatusClass = computed(() => {
  if (syncStatus.value.includes('mislukt') || syncStatus.value.includes('error')) {
    return 'text-orange-600 dark:text-orange-400'
  }
  return 'text-green-600 dark:text-green-400'
})

const simplicateSaleUrl = computed(() => {
  const id = props.prospect?.simplicate_id || props.prospect?.id
  if (!id) return '#'
  return `https://frisseblikken.simplicate.nl/sales/${id}`
})

// Start editing
const startEditingStart = () => {
  editingStartDate.value = toDateString(props.prospect.start_date)
  isEditingStartDate.value = true
  errorMessage.value = ''
}

const startEditingEnd = () => {
  editingEndDate.value = toDateString(props.prospect.expected_closing_date)
  isEditingEndDate.value = true
  errorMessage.value = ''
}

// Cancel editing
const cancelStartEdit = () => {
  isEditingStartDate.value = false
  editingStartDate.value = ''
  errorMessage.value = ''
}

const cancelEndEdit = () => {
  isEditingEndDate.value = false
  editingEndDate.value = ''
  errorMessage.value = ''
}

// Save handlers
const saveStartDate = () => {
  if (!editingStartDate.value) {
    errorMessage.value = 'Selecteer een datum'
    return
  }

  // Validate: start date should not be after end date
  const endDate = props.prospect.expected_closing_date?.split('T')[0]
  if (endDate && editingStartDate.value > endDate) {
    errorMessage.value = 'Startdatum mag niet na einddatum liggen'
    return
  }

  if (props.onUpdateDates) {
    props.onUpdateDates({
      start_date: editingStartDate.value
    })
  }
}

const saveEndDate = () => {
  if (!editingEndDate.value) {
    errorMessage.value = 'Selecteer een datum'
    return
  }

  // Validate: end date should not be before start date
  const startDate = props.prospect.start_date?.split('T')[0]
  if (startDate && editingEndDate.value < startDate) {
    errorMessage.value = 'Einddatum mag niet voor startdatum liggen'
    return
  }

  if (props.onUpdateDates) {
    props.onUpdateDates({
      expected_closing_date: editingEndDate.value
    })
  }
}

// Chance editing
const startEditingChance = () => {
  editingChance.value = props.prospect.chance_to_score ?? null
  isEditingChance.value = true
  errorMessage.value = ''
}

const cancelChanceEdit = () => {
  isEditingChance.value = false
  editingChance.value = null
  errorMessage.value = ''
}

const saveChance = () => {
  const val = editingChance.value
  if (val === null || val === undefined || val === '') {
    errorMessage.value = 'Vul een waarde in'
    return
  }
  if (!Number.isInteger(val) || val < 1 || val > 100) {
    errorMessage.value = 'Kans moet een geheel getal tussen 1 en 100 zijn'
    return
  }
  if (props.onUpdateChanceToScore) {
    props.onUpdateChanceToScore({ chance_to_score: val })
  }
}

// Expose methods for parent to control state
defineExpose({
  setLoading: loading => {
    isSaving.value = loading
  },
  setError: error => {
    errorMessage.value = error
  },
  setSyncStatus: status => {
    syncStatus.value = status
  },
  closeEditing: () => {
    isEditingStartDate.value = false
    isEditingEndDate.value = false
    isEditingChance.value = false
    editingStartDate.value = ''
    editingEndDate.value = ''
    editingChance.value = null
  }
})
</script>
